import { useEffect, useRef, useState } from "react";
import { CloseCircleOutlined, HistoryOutlined } from "@ant-design/icons";
import { fetchDataObjectHistory, type DataObjectRevision } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import type { SchemaFieldSummary, ToolPartProps } from "./types";

interface GetDataObjectHistoryInput {
    dataObjectId: string;
    description: string;
}

interface HistoryFieldChange {
    field: string;
    from: unknown;
    to: unknown;
}

interface HistoryRevisionSummary {
    version?: number;
    changeType?: string; // INSERT | MODIFY | REMOVE
    changedBy?: string;
    changedDate?: string;
    changes: HistoryFieldChange[];
}

interface GetDataObjectHistoryOutput {
    dataObjectId: string;
    revisionCount: number;
    revisions: HistoryRevisionSummary[];
    description: string;
}

function buildFieldIdToName(schemaFields: SchemaFieldSummary[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const f of schemaFields || []) {
        if (f?.fieldId && f?.name) map.set(f.fieldId, f.name);
    }
    return map;
}

function summariseRevision(rev: DataObjectRevision, fieldIdToName: Map<string, string>): HistoryRevisionSummary {
    const changes: HistoryFieldChange[] = [];
    const data = (rev?.data || {}) as Record<string, any>;
    const updated = rev?.changes?.updated || {};
    const added = rev?.changes?.added || {};
    const prevUpdated = rev?.previousChanges?.updated || {};

    // Updated fields: we know both the new value (changes.updated) and the
    // prior value (previousChanges.updated) for the same fieldId.
    for (const fieldId of Object.keys(updated)) {
        const name = fieldIdToName.get(fieldId);
        if (!name) continue;
        changes.push({ field: name, from: prevUpdated[fieldId], to: (updated as any)[fieldId] });
    }
    // Added fields (a field that first gets a value on this revision): only a
    // new value, no prior.
    for (const fieldId of Object.keys(added)) {
        const name = fieldIdToName.get(fieldId);
        if (!name) continue;
        if (Object.prototype.hasOwnProperty.call(updated, fieldId)) continue; // already covered
        changes.push({ field: name, from: undefined, to: (added as any)[fieldId] });
    }

    const hasChangeBlock = !!rev?.changes && (Object.keys(updated).length > 0 || Object.keys(added).length > 0 || Object.keys(rev?.changes?.deleted || {}).length > 0);

    return {
        version: rev?.metadata?.version ?? (data.version as number | undefined),
        changeType: hasChangeBlock ? "MODIFY" : "INSERT",
        changedBy: data.updatedByName || data.updatedByEmail || data.updatedBy || data.createdByName || data.createdByEmail || data.createdBy,
        changedDate: data.updatedDate || data.createdDate,
        changes,
    };
}

export default function GetDataObjectHistoryTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<GetDataObjectHistoryInput, GetDataObjectHistoryOutput>) {
    const { addToolOutput, tenantId, businessAreaId, schemaId, dataSet, schemaFields } = helpers;
    const resolvedRef = useRef(false);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        const dataObjectId = input?.dataObjectId;
        if (!dataObjectId) return;
        resolvedRef.current = true;

        const dataSetId = dataSet?.dataSetId;
        if (!tenantId || !businessAreaId || !schemaId || !dataSetId) {
            addToolOutput({
                tool: "getDataObjectHistory",
                toolCallId,
                output: buildToolError(
                    { message: "Missing dataset context (tenant, business area, schema, or dataset) to fetch the record history." },
                    "Could not load the record history because the dataset context is incomplete.",
                ),
            });
            return;
        }

        setPending(true);
        (async () => {
            try {
                const revisions = await fetchDataObjectHistory({ tenantId, businessAreaId, schemaId, dataSetId }, dataObjectId);
                const fieldIdToName = buildFieldIdToName(schemaFields);
                const summaries = revisions.map((r) => summariseRevision(r, fieldIdToName));
                addToolOutput({
                    tool: "getDataObjectHistory",
                    toolCallId,
                    output: {
                        dataObjectId,
                        revisionCount: summaries.length,
                        revisions: summaries,
                        description: input?.description || "",
                    } as GetDataObjectHistoryOutput,
                });
            } catch (e: any) {
                addToolOutput({
                    tool: "getDataObjectHistory",
                    toolCallId,
                    output: buildToolError(e?.response?.data || e, "Could not load the change history for that record. Please try again or open the record's history tab."),
                });
            } finally {
                setPending(false);
            }
        })();
    }, [state, input, tenantId, businessAreaId, schemaId, dataSet, schemaFields, addToolOutput, toolCallId]);

    const revisionCount = state === "output-available" && output ? output.revisionCount : undefined;
    const description = (state === "output-available" && output?.description) || input?.description || "";
    const sample = state === "output-available" && output ? output.revisions.slice(0, 5) : [];

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#001D3D]">
                <HistoryOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Record history</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            {pending && <div className="mt-1 text-xs text-zinc-500">Loading change history...</div>}
            {revisionCount !== undefined && (
                <div className="mt-1 text-xs text-zinc-500">
                    {revisionCount === 0
                        ? "No change history found for this record."
                        : `${revisionCount} revision${revisionCount === 1 ? "" : "s"} in this record's history.`}
                </div>
            )}
            {sample.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                    {sample.map((rev, idx) => (
                        <li key={`${rev.version ?? idx}`} className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] text-zinc-700">
                            <span className="font-mono text-[10px] text-zinc-500">v{rev.version ?? "?"}</span>
                            {" "}
                            <span className="uppercase text-[10px] text-zinc-400">{rev.changeType || "change"}</span>
                            {rev.changedDate ? <span className="text-[10px] text-zinc-400"> {" · "}{new Date(rev.changedDate).toLocaleString()}</span> : null}
                            {rev.changes.length > 0 && (
                                <span>{": "}{rev.changes.map((c) => c.field).join(", ")}</span>
                            )}
                        </li>
                    ))}
                    {revisionCount !== undefined && revisionCount > sample.length && (
                        <li className="text-[10px] text-zinc-400">...and {revisionCount - sample.length} more.</li>
                    )}
                </ul>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
